import { ethers } from 'ethers';
import { 
  LegalDocument, 
  CreateLegalDocumentInput,
  PaginationParams,
  PaginatedResponse 
} from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

const LEGACY_LEGAL_DOCS_ABI = [
  'function createDocument(uint8 docType, string memory documentHash, address[] memory signers, uint256 requiredSignatures, uint256 effectiveDate, uint256 expiryDate) public returns (uint256)',
  'function signDocument(uint256 documentId, string memory signature) public',
  'function getDocument(uint256 documentId) public view returns (uint8 docType, string memory documentHash, address creator, uint256 requiredSignatures, uint256 signatureCount, uint256 effectiveDate, uint256 expiryDate, uint256 createdAt, bool isActive)',
  'function activateSelfDestruct(uint256 documentId, uint8 destructType, uint256 destructValue) public',
  'function destroyDocument(uint256 documentId) public',
  'function recordView(uint256 documentId) public',
  'function isDocumentValid(uint256 documentId) public view returns (bool)'
];

export class LegalDocumentService {
  private client: LegacyChainClient;
  private contractAddress: string;

  constructor(client: LegacyChainClient) {
    this.client = client;
    this.contractAddress = process.env.NEXT_PUBLIC_LEGACY_LEGAL_DOCS_ADDRESS || '';
  }

  async create(input: CreateLegalDocumentInput): Promise<LegalDocument> {
    // Check storage limits
    const fileSizeMB = input.file.size / (1024 * 1024);
    const { allowed, remaining } = await this.client['rateLimiter'].checkStorageLimit(
      this.client.getConfig().apiKey,
      fileSizeMB
    );
    
    if (!allowed) {
      throw new Error(`Storage limit exceeded. ${remaining}MB remaining`);
    }

    let documentHash: string;
    let encryptionMetadata: any = {};

    // Handle encryption if requested
    if (input.encrypt) {
      const password = input.password || this.client.crypto.generatePassword();
      const fileContent = await this.fileToBase64(input.file);
      const { encrypted, salt, iv } = await this.client.crypto.encrypt(fileContent, password);
      
      // Upload encrypted document
      const blob = new Blob([JSON.stringify({ encrypted, salt, iv })], { type: 'application/json' });
      const encryptedFile = new File([blob], input.file.name, { type: 'application/json' });
      
      documentHash = await this.client.ipfs.uploadFile(encryptedFile, {
        type: 'legal-document',
        documentType: input.documentType,
        encrypted: true,
        originalName: input.file.name,
        ...input.metadata
      });

      encryptionMetadata = { encrypted: true, password };
    } else {
      // Upload unencrypted document
      documentHash = await this.client.ipfs.uploadFile(input.file, {
        type: 'legal-document',
        documentType: input.documentType,
        encrypted: false,
        ...input.metadata
      });
    }

    // Create on-chain document
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, signer);
    
    const docTypeMap: Record<string, number> = {
      'NDA': 0,
      'CONTRACT': 1,
      'WILL': 2,
      'AGREEMENT': 3,
      'CERTIFICATE': 4
    };
    
    const effectiveTimestamp = input.effectiveDate 
      ? Math.floor(input.effectiveDate.getTime() / 1000)
      : Math.floor(Date.now() / 1000);
    
    const expiryTimestamp = input.expiryDate
      ? Math.floor(input.expiryDate.getTime() / 1000)
      : 0;
    
    const tx = await contract.createDocument(
      docTypeMap[input.documentType],
      documentHash,
      input.signers,
      input.requiredSignatures || input.signers.length,
      effectiveTimestamp,
      expiryTimestamp
    );
    
    const receipt = await tx.wait();
    const documentId = receipt.logs[0].args.documentId.toString();

    // Set up self-destruct if configured
    if (input.selfDestruct) {
      await this.configureSelfDestruct(documentId, input.selfDestruct);
    }

    // Update storage counter
    await this.client['rateLimiter'].incrementStorage(this.client.getConfig().apiKey, fileSizeMB);

    // Create document object
    const document: LegalDocument = {
      id: documentId,
      documentHash,
      documentType: input.documentType,
      creator: await signer.getAddress(),
      signers: input.signers.map(address => ({
        address,
        signed: false
      })),
      requiredSignatures: input.requiredSignatures || input.signers.length,
      status: 'draft',
      effectiveDate: input.effectiveDate || new Date(),
      expiryDate: input.expiryDate,
      selfDestruct: input.selfDestruct,
      metadata: {
        title: input.file.name,
        parties: input.signers,
        encrypted: input.encrypt || false,
        fileType: input.file.type,
        fileSize: input.file.size,
        checksum: await this.calculateChecksum(input.file)
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Store in backend
    await this.client.makeRequest('POST', '/legal-documents', document);
    
    // Emit event
    this.client.emit('document:created', document);

    return { ...document, ...encryptionMetadata };
  }

  async sign(documentId: string, signature: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, signer);
    
    const tx = await contract.signDocument(documentId, signature);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('POST', `/legal-documents/${documentId}/sign`, { signature });
    
    // Emit event
    this.client.emit('document:signed', documentId, await signer.getAddress());
  }

  async generateSignature(documentId: string): Promise<string> {
    const document = await this.get(documentId);
    const signer = this.client.getSigner();
    
    const message = `I hereby sign document ${documentId} with hash ${document.documentHash}`;
    const signature = await signer.signMessage(message);
    
    return signature;
  }

  async get(documentId: string): Promise<LegalDocument> {
    const response = await this.client.makeRequest<LegalDocument>('GET', `/legal-documents/${documentId}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch document');
    }

    return response.data;
  }

  async list(params?: PaginationParams): Promise<PaginatedResponse<LegalDocument>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    }

    const response = await this.client.makeRequest<PaginatedResponse<LegalDocument>>(
      'GET',
      `/legal-documents?${queryParams.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch documents');
    }

    return response.data;
  }

  async recordView(documentId: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, signer);
    
    const tx = await contract.recordView(documentId);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('POST', `/legal-documents/${documentId}/view`);
  }

  async destroy(documentId: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, signer);
    
    const tx = await contract.destroyDocument(documentId);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('DELETE', `/legal-documents/${documentId}`);
  }

  async downloadDocument(document: LegalDocument, password?: string): Promise<Blob> {
    const blob = await this.client.ipfs.retrieveFile(document.documentHash);
    
    if (document.metadata?.encrypted) {
      if (!password) {
        throw new Error('Password required to decrypt document');
      }
      
      // Decrypt the document
      const encryptedData = await blob.text();
      const { encrypted, salt, iv } = JSON.parse(encryptedData);
      
      const decrypted = await this.client.crypto.decrypt(encrypted, password, salt, iv);
      
      // Convert base64 back to blob
      const binaryString = atob(decrypted.split(',')[1]);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      return new Blob([bytes], { type: document.metadata.fileType });
    }
    
    return blob;
  }

  async verifyDocument(documentId: string): Promise<boolean> {
    const provider = this.client.getProvider();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, provider);
    
    return contract.isDocumentValid(documentId);
  }

  private async configureSelfDestruct(documentId: string, config: any): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_LEGAL_DOCS_ABI, signer);
    
    const destructTypeMap: Record<string, number> = {
      'time': 0,
      'views': 1,
      'manual': 2
    };
    
    const value = config.type === 'time' && config.value instanceof Date
      ? Math.floor(config.value.getTime() / 1000)
      : config.value || 0;
    
    const tx = await contract.activateSelfDestruct(
      documentId,
      destructTypeMap[config.type],
      value
    );
    await tx.wait();
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private async calculateChecksum(file: File): Promise<string> {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
} 